import React, { useMemo, memo } from 'react';
import { useQuery } from '@apollo/client';
import capitalize from 'lodash/capitalize';
import { generatedTypesNew } from '@css-xp/graphtypes';
import {
  fetchProductAppSubscriptionsForTransactionAccount,
  fetchProductAppSubscriptionsForBillingProfile,
} from './queries/fetch-product-app-susbcriptions-txa';
import { ProductAppSubsriptionsTable } from '../../product-app-subscriptions-table';
import './product-app-subscriptions-container.scss';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { GenericErrorPage } from '../../error-page';

export interface ProductAppSubscriptionsContainerProps {
  transactionAccountId?: string;
  billingProfileId?: string;
  cloudSiteId?: string;
}

export const ProductAppSubscriptionsContainer: React.FC<ProductAppSubscriptionsContainerProps> = ({
  transactionAccountId,
  billingProfileId,
  cloudSiteId,
}) => {
  const filterOptions = !cloudSiteId
    ? { key: 'isNull', value: 'cloudSiteId' }
    : { key: 'cloudSiteId', value: cloudSiteId };

  const { data, loading, error } = useQuery<
    generatedTypesNew.FetchProductAppSubscriptionsForTransactionAccountQuery,
    generatedTypesNew.FetchProductAppSubscriptionsForTransactionAccountQueryVariables
  >(fetchProductAppSubscriptionsForTransactionAccount, {
    variables: {
      id: transactionAccountId as string,
      filterOptions,
    },
    skip: !transactionAccountId,
    nextFetchPolicy: 'cache-and-network',
  });

  const {
    data: productAppForBillingProfileData,
    loading: productAppForBillingProfileLoading,
    error: productAppForBillingProfileError,
  } = useQuery<
    generatedTypesNew.FetchProductAppSubscriptionsForBillingProfileQuery,
    generatedTypesNew.FetchProductAppSubscriptionsForBillingProfileQueryVariables
  >(fetchProductAppSubscriptionsForBillingProfile, {
    variables: {
      id: billingProfileId as string,
      filterOptions,
    },
    skip: !billingProfileId,
    nextFetchPolicy: 'cache-and-network',
  });

  const result = useMemo(
    () =>
      transactionAccountId
        ? data?.transactionAccount?.productAndAppSubscriptions
        : productAppForBillingProfileData?.invoiceGroup?.productAndAppSubscriptions,
    [transactionAccountId, data, productAppForBillingProfileData]
  );

  if (loading || productAppForBillingProfileLoading) {
    return (
      <div style={{ minHeight: '400px' }}>
        <LoadingIndicator />
      </div>
    );
  }

  const errorDetails = error || productAppForBillingProfileError;

  if (errorDetails) return <GenericErrorPage error={errorDetails} />;

  const getHeader = (key: string): string => {
    return ['jira', 'confluence'].includes(key) ? `${capitalize(key)} family` : 'Other products';
  };

  return (
    <div data-testid={'product-app-subscriptions-container'} id="product-app-subscriptions-container">
      {result &&
        Object.keys(result).map((key: string) => {
          //@ts-ignore
          if (!!result[key].products?.length || !!(result[key].apps?.length > 0)) {
            return (
              <div className="product-app-family-wrapper">
                {
                  //@ts-ignore
                  result[key].products?.length > 0 && (
                    <>
                      <div className="family-header">{getHeader(key)}</div>
                      <ProductAppSubsriptionsTable
                        //@ts-ignore
                        data={result[key].products}
                        billingProfileId={billingProfileId}
                      />
                    </>
                  )
                }
                {
                  //@ts-ignore
                  result[key].apps?.length > 0 && (
                    <>
                      <div className="app-header">{`Apps for ${capitalize(key)}`}</div>
                      <ProductAppSubsriptionsTable
                        //@ts-ignore
                        data={result[key].apps}
                        billingProfileId={billingProfileId}
                      />
                    </>
                  )
                }
              </div>
            );
          } else return null;
        })}
    </div>
  );
};

export const ProductAppSubscriptionsContainerWithMemo = memo(ProductAppSubscriptionsContainer);
